import React from 'react';
import { Download } from 'lucide-react';
import { toast } from 'react-toastify';

import { Button } from "@/components/ui/button";
import useApi from '../api';
import useAnalytics from '../analytics/analytics';
import toastConfig from '../config/Toast';
import { buildVisitorProfileParams } from './liveViewQuery';

type ExportVisitorsButtonProps = {
  params: URLSearchParams;
};

function escapeCsvValue(value: unknown): string {
  let stringValue = value == null ? '' : String(value);
  // Neutralise spreadsheet formulas in visitor-supplied cells.
  if (/^[=+\-@\t\r]/.test(stringValue)) stringValue = `'${stringValue}`;
  if (/[",\n]/.test(stringValue)) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
}

const ExportVisitorsButton: React.FC<ExportVisitorsButtonProps> = ({
  params: urlParams,
}) => {
  const { get } = useApi();
  const { captureEvent } = useAnalytics();

  const exportVisitors = async () => {
    try {
      const visitors: any[] = [];
      let page = 1;
      let pages = 1;
      do {
        const params = buildVisitorProfileParams({
          page,
          perPage: 500,
          search: urlParams.get('search') || '',
          statuses: urlParams.getAll('status'),
          lastSeenFilter: urlParams.get('last_seen_filter') || 'all',
          firstSeenFilter: urlParams.get('first_seen_filter') || 'all',
          identityType: urlParams.get('identity_type') || 'all',
          hasHistory: urlParams.get('has_conversation_history') || 'all',
          humanAgentIds: urlParams.getAll('human_agent_ids'),
          sortBy: (urlParams.get('sort_by') || 'last_seen_at') as any,
          sortOrder: urlParams.get('sort_order') === 'asc' ? 'asc' : 'desc',
          includeActivity: false,
        });
        const response = await get(`/visitor-profiles?${params.toString()}`);
        visitors.push(...(response.data?.items || []));
        pages = Math.max(Number(response.data?.pages) || 1, 1);
        page += 1;
      } while (page <= pages);

      const headers = [
        'Visitor',
        'Subtitle',
        'Status',
        'Last Seen',
        'First Seen',
        'Location',
        'Current Page',
        'Conversations',
        'Latest Session',
      ];

      const rows = visitors.map((visitor: any) => {
        const summary = visitor.identity?.summary || {};
        const location = [visitor.city, visitor.region, visitor.country].filter(Boolean).join(', ');
        return [
          summary.display_name || `Visitor ${String(visitor.visitor_id || '').slice(0, 8)}`,
          summary.subtitle || '',
          visitor.status_label || (visitor.online ? 'Onsite' : 'Offline'),
          visitor.last_seen || visitor.last_seen_at || '',
          visitor.first_seen_at || '',
          location,
          visitor.current_page || '',
          visitor.conversation_count || 0,
          visitor.latest_conversation_id || '',
        ].map(escapeCsvValue).join(',');
      });

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `visitors-export-${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Visitors exported successfully', toastConfig);
      captureEvent('export_visitors', {
        status: urlParams.getAll('status'),
        last_seen_filter: urlParams.get('last_seen_filter') || 'all',
      });
    } catch (error) {
      console.error('Error exporting visitors:', error);
      toast.error('Failed to export visitors', toastConfig);
    }
  };

  return (
    <Button variant="outline" onClick={exportVisitors}>
      <Download className="h-4 w-4 mr-2" />
      Export CSV
    </Button>
  );
};

export default ExportVisitorsButton;
